import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Eye, MousePointerClick, TrendingUp, Activity } from 'lucide-react';

const RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

type Event = {
  id: string;
  event_type: string;
  entity_type: string | null;
  entity_id: string | null;
  session_id: string | null;
  user_id: string | null;
  metadata: any;
  created_at: string;
};

const IMPRESSION_TYPES = ['stories_impression', 'viewed_projects_impression'];
const CLICK_TYPES = ['story_open', 'viewed_project_click'];

const SECTION_LABEL: Record<string, string> = {
  stories_impression: 'Stories — Impression',
  story_open: 'Stories — Open',
  viewed_projects_impression: 'Recently Viewed Projects — Impression',
  viewed_project_click: 'Recently Viewed Projects — Click',
};

const SECTION_PAIR = [
  { name: 'Stories', impression: 'stories_impression', click: 'story_open' },
  { name: 'Recently Viewed Projects', impression: 'viewed_projects_impression', click: 'viewed_project_click' },
];

export default function LandingAnalytics() {
  const [days, setDays] = useState(30);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['landing-analytics-events', days],
    queryFn: async () => {
      const since = new Date(Date.now() - days * 86400000).toISOString();
      const { data } = await supabase
        .from('landing_engagement_events' as any)
        .select('id, event_type, entity_type, entity_id, session_id, user_id, metadata, created_at')
        .gte('created_at', since)
        .limit(50000);
      return (data || []) as unknown as Event[];
    },
  });

  const totals = useMemo(() => {
    const t: Record<string, number> = {};
    for (const e of events) t[e.event_type] = (t[e.event_type] || 0) + 1;
    const totalImpressions = IMPRESSION_TYPES.reduce((s, k) => s + (t[k] || 0), 0);
    const totalClicks = CLICK_TYPES.reduce((s, k) => s + (t[k] || 0), 0);
    return { byType: t, totalImpressions, totalClicks };
  }, [events]);

  const trend = useMemo(() => {
    const buckets = new Map<string, any>();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
      buckets.set(d, { date: d.slice(5), impressions: 0, clicks: 0 });
    }
    for (const e of events) {
      const day = e.created_at.slice(0, 10);
      const row = buckets.get(day);
      if (!row) continue;
      if (IMPRESSION_TYPES.includes(e.event_type)) row.impressions += 1;
      else if (CLICK_TYPES.includes(e.event_type)) row.clicks += 1;
    }
    return [...buckets.values()];
  }, [events, days]);

  const sectionRows = useMemo(() => {
    return SECTION_PAIR.map(s => {
      const imp = totals.byType[s.impression] || 0;
      const clk = totals.byType[s.click] || 0;
      const ctr = imp > 0 ? (clk / imp) * 100 : 0;
      return { ...s, imp, clk, ctr };
    });
  }, [totals]);

  const topEntities = useMemo(() => {
    const map = new Map<string, { type: string; id: string; clicks: number }>();
    for (const e of events) {
      if (!CLICK_TYPES.includes(e.event_type) || !e.entity_id) continue;
      const k = `${e.entity_type}:${e.entity_id}`;
      const cur = map.get(k) || { type: e.entity_type || '', id: e.entity_id, clicks: 0 };
      cur.clicks += 1;
      map.set(k, cur);
    }
    return [...map.values()].sort((a, b) => b.clicks - a.clicks).slice(0, 10);
  }, [events]);

  const overallCtr = totals.totalImpressions > 0
    ? ((totals.totalClicks / totals.totalImpressions) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Landing Analytics</h1>
          <p className="text-sm text-muted-foreground">Impressions and clicks for landing-page sections.</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RANGES.map(r => <SelectItem key={r.days} value={String(r.days)}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><Eye className="h-5 w-5 text-primary" /></div>
            <div><div className="text-xs text-muted-foreground">Impressions</div><div className="font-bold text-xl">{totals.totalImpressions.toLocaleString()}</div></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><MousePointerClick className="h-5 w-5 text-primary" /></div>
            <div><div className="text-xs text-muted-foreground">Clicks</div><div className="font-bold text-xl">{totals.totalClicks.toLocaleString()}</div></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-primary" /></div>
            <div><div className="text-xs text-muted-foreground">Overall CTR</div><div className="font-bold text-xl">{overallCtr}%</div></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><Activity className="h-5 w-5 text-primary" /></div>
            <div><div className="text-xs text-muted-foreground">Events</div><div className="font-bold text-xl">{events.length.toLocaleString()}</div></div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Daily Impressions vs Clicks</CardTitle></CardHeader>
        <CardContent style={{ height: 320 }}>
          {isLoading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="impressions" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="clicks" stroke="hsl(var(--destructive))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>By Section</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-4">Section</th>
                <th className="py-2 pr-4">Impressions</th>
                <th className="py-2 pr-4">Clicks</th>
                <th className="py-2 pr-4">CTR</th>
              </tr>
            </thead>
            <tbody>
              {sectionRows.map(r => (
                <tr key={r.name} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium">{r.name}</td>
                  <td className="py-2 pr-4">{r.imp.toLocaleString()}</td>
                  <td className="py-2 pr-4">{r.clk.toLocaleString()}</td>
                  <td className="py-2 pr-4">{r.imp > 0 ? r.ctr.toFixed(1) : '0.0'}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Top Clicked Entities</CardTitle></CardHeader>
        <CardContent>
          {topEntities.length === 0 ? (
            <div className="text-sm text-muted-foreground">No clicks recorded in this range.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {topEntities.map((t, i) => (
                <li key={`${t.type}-${t.id}`} className="flex items-center justify-between border-b last:border-0 pb-2">
                  <span className="truncate">
                    <span className="text-muted-foreground mr-2">#{i + 1}</span>
                    <span className="capitalize">{t.type}</span>
                    <span className="text-muted-foreground"> · {t.id.slice(0, 8)}…</span>
                  </span>
                  <span className="font-semibold">{t.clicks} clicks</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
